// src/store/cashStore.ts
// ─────────────────────────────────────────────────────────────────────────────
// NIIZ ERP · Store de Caja / Turnos (Z-Report)
// ─────────────────────────────────────────────────────────────────────────────
// Actualización v110:
//   · Cero `any` — tipado estricto con CashShift/Sale de db.ts
//   · Conexión con motor financiero (Centavos)
//   · Soporte multi-sede (branch_id) y clasificación flexible (tags)
//   · 100% aditivo: toda la lógica de negocio existente se preserva
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { db, type CashShift, type Sale } from '@/db/db';
import { useAuthStore } from '@/store/authStore';
import type { UploadBatchResult } from '@/lib/cloudUploader';
import { accumulateTips, listTipsStaff, splitTipsEqually, sumShiftTips, readUserBranchId, readSaleBranchId } from '@/lib/tipsDistribution';
import { C, type Centavos } from '@/core/financial-engine/money';

// ─────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────

interface ShiftCloseData {
  final_actual: number;
  total_sales_cash: number;
  total_sales_card: number;
  total_expenses: number;
  notes?: string;
  /** 🆕 ID de la sucursal donde se cierra el turno (multi-sede). */
  branch_id?: string;
  /** 🆕 Etiquetas para clasificación flexible (ej: ["cierre-nocturno"]). */
  tags?: string[];
}

/** Reparto de propinas persistido en `CashShift.tips_breakdown` (db.ts). */
type CashShiftTipsBreakdown = NonNullable<CashShift['tips_breakdown']>;

/** Reporte de auditoría del cierre de caja (se serializa en JSON en `notes`). */
interface ShiftAuditReport {
  closed_at: string;
  base: number;
  sales_cash: number;
  sales_card: number;
  expenses: number;
  expected_cash: number;
  counted_cash: number;
  difference: number;
  propinas: {
    total_turno: number;
    beneficiarios: number;
    repartidas: number;
  };
  tips_breakdown: CashShiftTipsBreakdown;
  notes: string | null;
}

interface CashState {
  // Estado
  isShiftOpen: boolean;
  currentShiftId: string | null;
  currentShift: CashShift | null;
  baseAmount: number;
  openedAt: string | null;
  loading: boolean;
  error: string | null;

  // Acciones
  checkShiftStatus: () => Promise<void>;
  openShift: (initialAmount: number, userId: string) => Promise<void>;
  /** Cierra el turno (Z-Report) y dispara la subida por lotes consolidada. */
  closeShift: (data: ShiftCloseData) => Promise<UploadBatchResult | null>;

  // Helper
  getCurrentShift: () => Promise<CashShift | undefined>;

  // ── 🆕 HELPERS FINANCIEROS (Centavos) ──
  /** Monto base del turno actual en Centavos. */
  getBaseAmountInCents: () => Centavos;
  /** Diferencia del último cierre (sobrante/faltante) en Centavos. */
  getLastDifferenceInCents: () => Centavos;
}

// ─────────────────────────────────────────────────────────────
// HELPERS DE CONVERSIÓN FINANCIERA
// ─────────────────────────────────────────────────────────────

/** Convierte un monto en pesos a Centavos. */
const toCentavos = (amount: number): Centavos => C(BigInt(Math.round(amount * 100)));

// ─────────────────────────────────────────────────────────────
// STORE NIIZ CASH
// ─────────────────────────────────────────────────────────────
export const useCashStore = create<CashState>((set, get) => ({
  isShiftOpen: false,
  currentShiftId: null,
  currentShift: null,
  baseAmount: 0,
  openedAt: null,
  loading: true,
  error: null,

  // ─── Verificar estado de caja al iniciar ───
  checkShiftStatus: async () => {
    set({ loading: true, error: null });

    try {
      const openShift = await db.cash_shifts
        .where('status')
        .equals('OPEN')
        .first();

      if (openShift) {
        set({
          isShiftOpen: true,
          currentShiftId: openShift.id ?? null,
          currentShift: openShift,
          baseAmount: openShift.initial_amount,
          openedAt: openShift.opened_at,
          loading: false,
        });
      } else {
        set({
          isShiftOpen: false,
          currentShiftId: null,
          currentShift: null,
          baseAmount: 0,
          openedAt: null,
          loading: false,
        });
      }
    } catch (err) {
      console.error('Error verificando turno:', err);
      set({ error: 'No se pudo verificar el estado de la caja', loading: false });
    }
  },

  // ─── Abrir turno ───
  openShift: async (initialAmount, userId) => {
    const { isShiftOpen } = get();

    const user = useAuthStore.getState().user;
    const businessId = user?.business_id || 'local';
    // Multi-sede: `User`/`UserProfile` NO declara `branch_id` (db.ts). La sede
    // del turno se deriva de `shift.business_id` + `db.branches.is_main`, que
    // crea la migración v109; no se inventa una columna nueva.
    const branchId = readUserBranchId(user);
    if (branchId && !businessId) {
      throw new Error('La sesión no tiene negocio asociado');
    }

    if (isShiftOpen) {
      throw new Error('Ya existe un turno abierto');
    }

    if (initialAmount < 0) {
      throw new Error('El monto base no puede ser negativo');
    }

    set({ loading: true, error: null });

    try {
      const now = new Date().toISOString();
      const shiftId = crypto.randomUUID();

      const newShift: CashShift = {
        id: shiftId,
        business_id: businessId,
        status: 'OPEN',
        opened_at: now,
        opened_by: userId,
        initial_amount: initialAmount,
        notes: null,
        synced: false,
        sync_deferred_reason: null,
        total_tips: 0,
        tips_breakdown: null,
      };

      await db.cash_shifts.add(newShift);

      set({
        isShiftOpen: true,
        currentShiftId: shiftId,
        currentShift: newShift,
        baseAmount: initialAmount,
        openedAt: now,
        loading: false,
      });
    } catch (err) {
      console.error('Error abriendo turno:', err);
      set({ error: 'No se pudo abrir el turno', loading: false });
      throw err;
    }
  },

  // ─── Cerrar turno (Z-Report) ───
  closeShift: async (data) => {
    const { currentShiftId, baseAmount } = get();
    if (!currentShiftId) {
      throw new Error('No hay turno abierto para cerrar');
    }

    set({ loading: true, error: null });

    try {
      const now = new Date().toISOString();
      const expected = baseAmount + data.total_sales_cash - data.total_expenses;
      const difference = data.final_actual - expected;

      // 🪙 PROPINAS DEL TURNO: total acumulado + reparto equitativo
      // `tips_breakdown` de CashShift es `{ user_id; name; role?: string; amount }`.
      let totalTips = 0;
      let tipsBreakdown: CashShiftTipsBreakdown = [];
      try {
        const shift = await db.cash_shifts.get(currentShiftId);
        const shiftSales: Sale[] = shift?.opened_at
          ? await db.sales.where('created_at').aboveOrEqual(shift.opened_at).toArray()
          : [];
        // Multi-sede: si el usuario trae sede y las ventas legacy la registran,
        // el reparto se limita a las ventas de esa sede.
        const saleBranchId = readUserBranchId(useAuthStore.getState().user);
        const scopedSales: Sale[] =
          saleBranchId === undefined
            ? shiftSales
            : shiftSales.filter((s) => {
                const saleBranch = readSaleBranchId(s);
                return saleBranch === undefined || saleBranch === saleBranchId;
              });
        totalTips = sumShiftTips(scopedSales);
        if (totalTips > 0) {
          const staff = await listTipsStaff();
          const assignments = splitTipsEqually(totalTips, staff);
          // La firma de `TipAssignment` exige `role: string`; el campo de la
          // tabla es opcional, así que el objeto sigue siendo asignable.
          tipsBreakdown = assignments.map((t) => ({
            user_id: t.user_id,
            name: t.name,
            role: t.role,
            amount: t.amount,
          }));
        }
      } catch (e) {
        console.warn('No se pudieron calcular las propinas del turno:', e);
      }

      const auditReport: ShiftAuditReport = {
        closed_at: now,
        base: baseAmount,
        sales_cash: data.total_sales_cash,
        sales_card: data.total_sales_card,
        expenses: data.total_expenses,
        expected_cash: expected,
        counted_cash: data.final_actual,
        difference,
        propinas: {
          total_turno: totalTips,
          beneficiarios: tipsBreakdown.length,
          repartidas: tipsBreakdown.reduce((sum, t) => sum + t.amount, 0),
        },
        tips_breakdown: tipsBreakdown,
        notes: data.notes || null,
      };

      // Actualizar el turno en Dexie (tipado estricto, sin `as any`)
      await db.cash_shifts.update(currentShiftId, {
        status: 'CLOSED',
        closed_at: now,
        final_amount: data.final_actual,
        notes: JSON.stringify(auditReport),
        total_tips: totalTips,
        tips_breakdown: tipsBreakdown,
        synced: false,
        sync_deferred_reason: null,
      });

      // 🪙 La propina asignada se abona al acumulado de cada trabajador.
      if (tipsBreakdown.length > 0) {
        await accumulateTips(
          tipsBreakdown.map((t) => ({
            user_id: t.user_id,
            name: t.name,
            role: t.role ?? '',
            amount: t.amount,
          })),
        );
      }

      set({
        isShiftOpen: false,
        currentShiftId: null,
        currentShift: null,
        baseAmount: 0,
        openedAt: null,
        loading: false,
      });

      // ── SINCRONIZACIÓN AL CIERRE DE CAJA (Z-Report) ──
      try {
        const { flushAtCashClose } = await import('@/lib/cloudUploader');
        return await flushAtCashClose();
      } catch (syncErr) {
        console.warn('Cierre de caja en local: el lote queda encolado para reintento', syncErr);
        return null;
      }
    } catch (err) {
      console.error('Error cerrando turno:', err);
      set({ error: 'No se pudo cerrar el turno', loading: false });
      throw err;
    }
  },

  // ─── Helper: obtener detalles del turno actual ───
  getCurrentShift: async () => {
    const { currentShiftId } = get();
    if (!currentShiftId) return undefined;
    return await db.cash_shifts.get(currentShiftId);
  },

  // ── 🆕 HELPERS FINANCIEROS (Centavos) ──

  getBaseAmountInCents: () => {
    const { baseAmount } = get();
    return toCentavos(baseAmount);
  },

  getLastDifferenceInCents: () => {
    const { currentShift } = get();
    if (!currentShift?.notes) return C(0n);
    try {
      const report = JSON.parse(currentShift.notes) as ShiftAuditReport;
      return toCentavos(report.difference ?? 0);
    } catch {
      return C(0n);
    }
  },
}));

// ─────────────────────────────────────────────────────────────
// HELPERS PÚBLICOS (fuera del store para uso en componentes)
// ─────────────────────────────────────────────────────────────

/**
 * Calcula el monto esperado en caja al cierre en Centavos.
 * expected = base + ventas_efectivo - gastos
 */
export const calculateExpectedCashInCents = (
  baseAmount: number,
  totalSalesCash: number,
  totalExpenses: number,
): Centavos => {
  const expected = baseAmount + totalSalesCash - totalExpenses;
  return toCentavos(expected);
};

/**
 * Calcula la diferencia (sobrante/faltante) en Centavos.
 * Positivo = sobrante, negativo = faltante.
 */
export const calculateDifferenceInCents = (
  finalActual: number,
  expected: number,
): Centavos => {
  const diff = finalActual - expected;
  return toCentavos(diff);
};

/**
 * Calcula el total de ventas del turno (efectivo + tarjeta) en Centavos.
 */
export const calculateTotalSalesInCents = (
  totalSalesCash: number,
  totalSalesCard: number,
): Centavos => {
  return toCentavos(totalSalesCash + totalSalesCard);
};

/**
 * Calcula el porcentaje de diferencia respecto al esperado.
 * Útil para mostrar alertas de sobrante/faltante.
 * @returns Número entre -1 y 1 (ej: 0.05 = 5% de diferencia)
 */
export const calculateDifferencePercentage = (
  finalActual: number,
  expected: number,
): number => {
  if (expected === 0) return finalActual === 0 ? 0 : 1;
  return (finalActual - expected) / expected;
};